import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 80%;
  align-self: ${props => props.$role === 'user' ? 'flex-end' : 'flex-start'};
  margin-bottom: 16px;
`;

const MessageBubble = styled.div`
  padding: 12px 16px;
  border-radius: 18px;
  background-color: ${props => props.$role === 'user'
    ? 'var(--primary-color)'
    : 'rgba(240, 242, 245, 0.9)'};
  color: ${props => props.$role === 'user' ? 'white' : 'var(--primary-color)'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  font-size: 15px;
  line-height: 1.5;
  margin-top: 8px;

  /* Markdown styling */
  & p {
    margin: 0.5em 0;
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }

  & ul, & ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  & li {
    margin: 0.25em 0;
  }

  & a {
    color: ${props => props.$role === 'user' ? 'white' : 'var(--primary-color)'};
    text-decoration: underline;
    word-break: break-word;
  }

  & code {
    font-family: monospace;
    background-color: ${props => props.$role === 'user'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.05)'};
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-size: 0.9em;
  }

  & pre {
    background-color: ${props => props.$role === 'user'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.05)'};
    padding: 0.5em;
    border-radius: 5px;
    overflow-x: auto;
    margin: 0.5em 0;
  }

  & pre code {
    background-color: transparent;
    padding: 0;
  }

  & blockquote {
    border-left: 3px solid ${props => props.$role === 'user'
      ? 'rgba(255, 255, 255, 0.5)'
      : 'rgba(0, 0, 0, 0.1)'};
    margin: 0.5em 0;
    padding-left: 1em;
    font-style: italic;
  }

  & h1, & h2, & h3, & h4, & h5, & h6 {
    margin: 0.5em 0;
    font-weight: 600;
  }

  & hr {
    border: none;
    border-top: 1px solid ${props => props.$role === 'user'
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0, 0, 0, 0.1)'};
    margin: 0.5em 0;
  }

  & table {
    border-collapse: collapse;
    margin: 0.5em 0;
    width: 100%;
  }

  & th, & td {
    border: 1px solid ${props => props.$role === 'user'
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0, 0, 0, 0.1)'};
    padding: 0.3em 0.5em;
    text-align: left;
  }

  & th {
    background-color: ${props => props.$role === 'user'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.05)'};
  }

  /* Ensure images don't exceed container width */
  & img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 0.5em 0;
  }
`;

const MessageRole = styled.div`
  font-size: 12px;
  margin-bottom: 4px;
  color: var(--primary-color-transparent);
  font-weight: 500;
  text-transform: capitalize;
  display: ${props => props.$hideRole ? 'none' : 'block'};
`;

const UserIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--primary-color-transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--primary-color);
  font-weight: bold;
  align-self: flex-end;
`;

const AssistantIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  font-weight: bold;
  align-self: flex-start;
`;

const MessageWithIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
`;

const LoadingDots = styled.span`
  display: inline-block;

  &::after {
    content: '.';
    animation: dots 1.5s steps(5, end) infinite;
  }

  @keyframes dots {
    0%, 20% {
      content: '.';
    }
    40% {
      content: '..';
    }
    60% {
      content: '...';
    }
    80%, 100% {
      content: '';
    }
  }
`;

// Allowed image hosts — must stay in sync with the backend prompt whitelist
// (backend/server.js "CRITICAL: ONLY use image URLs that start with...")
const ALLOWED_IMAGE_PREFIXES = [
  'https://muspt.s3.eu-central-1.amazonaws.com/',
  'https://boavista.mus.pt/',
];

const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return ALLOWED_IMAGE_PREFIXES.some(prefix => url.startsWith(prefix));
};

const Message = ({ $role, content, isLoading }) => {
  const isUser = $role === 'user';

  return (
    <MessageContainer $role={$role}>
      <MessageRole $hideRole={true}>{$role}</MessageRole>
      <MessageWithIcon $isUser={isUser}>
        {isUser ? (
          <UserIcon>U</UserIcon>
        ) : (
          <AssistantIcon>M</AssistantIcon>
        )}
        <MessageBubble $role={$role}>
          {isLoading ? (
            <LoadingDots />
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // This ensures links open in a new tab
                a: ({ node, children, ...props }) => (
                  <a target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                ),
                // Custom image component to validate URLs and ensure proper sizing
                img: ({ node, ...props }) => {
                  // Only render images with valid URLs
                  if (isValidImageUrl(props.src)) {
                    return (
                      <img
                        {...props}
                        alt={props.alt || 'Image'}
                        onError={(e) => {
                          // Hide image on load error
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  }
                  // Return null for invalid URLs to prevent rendering
                  return null;
                }
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </MessageBubble>
      </MessageWithIcon>
    </MessageContainer>
  );
};

export default Message;
